import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { Users } from '../types';
import { User } from '@app/shared/types';
import { get, put, post, del } from '../../../shared/api/apiService';
import { useApiEssentials } from '../../../shared/hooks/useApiEssentials';

export interface UseUsersOptions {
  pageSize: number;
  page: number;
  filters?: Array<{
    field: string;
    value: any;
  }>;
  search?: string;
  sort?: Array<{
    field: string;
    direction: 'ASC' | 'DESC';
  }>;
}

type FilterTransform = (value: any) => Record<string, any>;

const FILTER_TRANSFORMS: Record<string, FilterTransform> = {
  isActive: value => ({ isActive: value === true || value === 'true' }),
  isInternal: value => ({ isInternal: value === true || value === 'true' }),
};

function buildUsersFilter(
  filters: Array<{ field: string; value: any }>,
  search: string,
): Record<string, any> {
  const filter: Record<string, any> = {};
  if (search) filter.search = search;

  for (const { field, value } of filters) {
    if (value === undefined || value === null || value === '') continue;

    const transform = FILTER_TRANSFORMS[field];
    Object.assign(filter, transform ? transform(value) : { [field]: value });
  }

  return filter;
}

export function useUsers({
  pageSize,
  page,
  filters = [],
  search = '',
  sort = [],
}: UseUsersOptions) {
  const { getIdToken, apiUrl, isSignedIn } = useApiEssentials();

  return useQuery({
    queryKey: ['users', { pageSize, page, filters, search, sort }],
    queryFn: async () => {
      const idToken = await getIdToken();
      return await post<Users>(
        `/users/page`,
        {
          limit: pageSize,
          page,
          sort,
          filter: buildUsersFilter(filters, search),
        },
        apiUrl,
        idToken,
      );
    },
    enabled: isSignedIn,
    placeholderData: keepPreviousData,
    gcTime: 300000,
    refetchOnMount: false,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

export function useSearchUsers(search: string) {
  const { getIdToken, apiUrl, isSignedIn } = useApiEssentials();
  const trimmedSearch = search.trim();

  return useQuery({
    queryKey: ['users-search', trimmedSearch],
    queryFn: async () => {
      const idToken = await getIdToken();
      const response = await post<Users>(
        `/users/page`,
        {
          limit: 10,
          page: 0,
          sort: [],
          filter: { username: trimmedSearch },
        },
        apiUrl,
        idToken,
      );
      return response.elements;
    },
    enabled: isSignedIn && trimmedSearch.length > 0,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

export function useUser(
  userId?: string,
  options?: { refetchOnWindowFocus?: boolean; refetchOnReconnect?: boolean },
) {
  const { getIdToken, apiUrl, isSignedIn } = useApiEssentials();

  return useQuery({
    queryKey: ['user', userId],
    queryFn: async () => {
      const idToken = await getIdToken();
      if (!userId) throw new Error('User ID is required');
      return await get<User>(`/users/${userId}`, apiUrl, idToken);
    },
    enabled: isSignedIn && !!userId,
    retry: false,
    refetchOnWindowFocus: options?.refetchOnWindowFocus ?? false,
    refetchOnReconnect: options?.refetchOnReconnect ?? false,
  });
}

export function useCreateUser() {
  const { getIdToken, apiUrl } = useApiEssentials();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (newUser: User) => {
      const idToken = await getIdToken();
      return await post<User>('/users', newUser, apiUrl, idToken);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
  });
}

export function useUpdateUser() {
  const { getIdToken, apiUrl } = useApiEssentials();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      userData,
    }: {
      userId: string;
      userData: User;
    }) => {
      const idToken = await getIdToken();
      if (userData.isActive !== undefined) {
        await put<User>(
          `/users/${userId}/status`,
          { isActive: userData.isActive },
          apiUrl,
          idToken,
        );
      }
      return await put<User>(`/users/${userId}`, userData, apiUrl, idToken);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user', variables.userId] });
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
  });
}

export function useDeleteUser() {
  const { getIdToken, apiUrl } = useApiEssentials();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const idToken = await getIdToken();
      return await del<User>(`/users/${userId}`, apiUrl, idToken);
    },
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.removeQueries({ queryKey: ['user', userId] });
    },
  });
}
